import React from 'react';
import once from 'lodash.once';
import PropTypes from 'prop-types';
import classNames from 'classnames'

import { CHANGE_STYLE_EVENT, DESKTOP_BREAKPOINT, MESO_AD_EVENTS, MESO_SLOT_STATUSES} from '../../constants'
import {Context} from '../MesoAdsContext/MesoAdsContext';
import {Observer} from '../Observer/Observer';
import {Placeholder} from '../Placeholder/Placeholder';
import {ErrorBoundary} from '../ErrorBoundary/ErrorBoundary';
import {
    emitAdView,
    emitAdFailed,
    emitAdSucceeded
} from './events';

import './MesoAds.less';

const isMobileBreakpoint = () => {
    return window.innerWidth < DESKTOP_BREAKPOINT;
};

class MesoSlot extends React.Component {
    constructor(props) {
        super(props);
        this.onMesoAd = this.onMesoAd.bind(this);
        this.onError = this.onError.bind(this);
        this.onVisibleOnce = once(this.onVisible.bind(this));

        this.mesoDisplayAdRef = React.createRef();
        this.state = {
            status: MESO_SLOT_STATUSES.LOADING
        };
    }

    componentDidMount() {
        this.props.onAdLoading && this.props.onAdLoading();
        this.mesoDisplayAdRef.current.addEventListener("mesoAd", this.onMesoAd);
    }

    componentWillUnmount() {
        this.mesoDisplayAdRef.current.removeEventListener("mesoAd", this.onMesoAd);
    }

    onMesoAd(event) {
        const {props: {gaiaId, slot, onAdLoadSuccess, onAdLoadFailed}} = this;

        const {code, uci, src} = event.detail;

        const newState = {
            adUci: uci,
            adSrc: src
        };

        if (MESO_AD_EVENTS.PASS === code) {
            emitAdSucceeded({gaiaId, adUci: uci, adSrc: src, adPlacement: slot});
            newState.status = MESO_SLOT_STATUSES.LOADED;
            onAdLoadSuccess && onAdLoadSuccess();

            this.handleBRLBreakPoint();

        } else if (MESO_AD_EVENTS.FAIL === code || MESO_AD_EVENTS.EMPTY === code) {
            emitAdFailed({failedReason: code, gaiaId, adUci: uci, adSrc: src, adPlacement: slot})
            newState.status = MESO_SLOT_STATUSES.FAILED;
            onAdLoadFailed && onAdLoadFailed();
        }

        this.setState(newState);
    }

    handleBRLBreakPoint() {
        if (this.props.uci !== 'BRL') {
            return;
        }

        this.postBreakpointToIframe();

        window.onresize = () => this.postBreakpointToIframe();
    }

    postBreakpointToIframe() {
        const isMobile = isMobileBreakpoint();

        if(this.state.isMobile === isMobile) {
        return;
        }

        this.setState({isMobile})
        const mesoNative = this.mesoDisplayAdRef.current.shadowRoot;
        const mesoGPTFluid = mesoNative.firstElementChild;
        const mesoGPT = mesoGPTFluid.shadowRoot.firstElementChild;
        const iframe = mesoGPT.shadowRoot.children[0].getElementsByTagName('iframe')[0];

        iframe.contentWindow.postMessage({ type: CHANGE_STYLE_EVENT, details: isMobile ? { add: 'mobile', remove: 'desktop' } : { add: 'desktop', remove: 'mobile' } }, '*');
    }

    get loadSuccess() {
        return this.state.status === MESO_SLOT_STATUSES.LOADED;
    }

    getContainerClasses() {
        const {slot, strategy} = this.props;
        const slotClass = `meso-ad-${slot}`;
        const classes = {
            [slotClass]: true
        };

        if (strategy === 'expand') {
            classes[slotClass] = this.loadSuccess;
            classes['meso-ad-loading'] = !this.loadSuccess && this.state.status !== MESO_SLOT_STATUSES.FAILED;
            classes['meso-ad-failed'] = this.state.status === MESO_SLOT_STATUSES.FAILED;
        }
        return classNames('meso-ad-container', classes);
    }

    onError() {
        const {slot, gaiaId} = this.props;
        const {adUci, adSrc} = this.state;

        emitAdFailed({
            failedReason: MESO_SLOT_STATUSES.RENDERING_FAILURE,
            adPlacement: slot,
            gaiaId,
            adUci,
            adSrc
        });
    }

    onVisible() {
        const {slot, gaiaId} = this.props;
        const {adUci, adSrc} = this.state;

        emitAdView({
            adPlacement: slot,
            gaiaId,
            adUci,
            adSrc
        });
    }

    render() {
        const {slot, uci, style = {}, nativeType, renderPlaceholder} = this.props;
        const slotClasses = this.getContainerClasses();
        return (
            <ErrorBoundary onError={this.onError}>
                <div className={slotClasses} style={style}>
                    {renderPlaceholder && <Placeholder extraClasses="meso-ad-placeholder"/>}
                    {nativeType ?
                         <meso-native
                            ref={this.mesoDisplayAdRef}
                            uci={uci}
                            adslot={slot}
                            nativetype={nativeType}
                            isfluid="true"
                            timeout={4000} />
                         :
                         <meso-display-ad
                            ref={this.mesoDisplayAdRef}
                            uci={slot}
                            slot={slot} />
                    }
                            {this.loadSuccess && <Observer onVisible={this.onVisibleOnce}>
                            {(ref) => <div ref={ref} />}
                        </Observer>}
                </div>
            </ErrorBoundary>
        );
    }
}

export const MesoAds = function ({slot, uci, nativeType, strategy, onAdLoadSuccess, onAdLoadFailed, onAdLoading, style, renderPlaceholder}) {
    return (
        <Context.Consumer>
            {({allowAds, gaiaId}) => allowAds ?
                <MesoSlot
                    gaiaId={gaiaId}
                    slot={slot}
                    uci={uci}
                    nativeType={nativeType}
                    strategy={strategy}
                    onAdLoadSuccess={onAdLoadSuccess}
                    onAdLoadFailed={onAdLoadFailed}
                    onAdLoading={onAdLoading}
                    style={style}
                    renderPlaceholder={renderPlaceholder}
                />
                : null
            }
        </Context.Consumer>
    );
};

MesoAds.defaultProps = {
    strategy: 'expand',
    renderPlaceholder: true
};

MesoAds.propTypes = {
    slot: PropTypes.string,
    uci: PropTypes.string,
    nativeType: PropTypes.string,
    strategy: PropTypes.oneOf([
        'expand', 'collapse'
    ]),
    onAdLoadSuccess: PropTypes.func,
    onAdLoadFailed: PropTypes.func,
    onAdLoading: PropTypes.func,
    style: PropTypes.object,
    renderPlaceholder: PropTypes.bool
};
